import React, { useState, useCallback, useEffect } from 'react'

import { UseFormReturn } from 'react-hook-form'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { BusinessCategory } from '@/core/api/entities/businessCategory'
import { DemographicAgeGroup } from '@/core/api/entities/demographicAgeGroup'
// import { DemographicGender } from '@/core/api/entities/demographicGender'
import { DemographicSocioeconomicGroup } from '@/core/api/entities/demographicSocioeconomicGroup'
import { useBrandIdentities } from '@/core/hooks/useBrandIdentities'
import { useTranslation } from '@/core/i18n/useTranslation'
import {
	OnboardingStepMeta,
	useOnboardingStepTracking,
} from '@/shared/analytics/useOnboardingStepTracking'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/atoms/Alert'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Spinner } from '@/shared/components/atoms/Spinner'
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal/ConfirmationModal'

import { OnbardingLayout } from '../OnbardingLayout'
import { useRegisterFlow } from './RegisterFlowContext'
import { RegisterStep0 } from './RegisterStep0'
import { RegisterStep1 } from './RegisterStep1'
import { RegisterStep2 } from './RegisterStep2'
import { RegisterStep3 } from './RegisterStep3'
import { RegisterStep4 } from './RegisterStep4'
import { RegisterStep5 } from './RegisterStep5'
import { RegisterStep6 } from './RegisterStep6'
import { RegisterStep7 } from './RegisterStep7'
import { RegisterStepLoading } from './RegisterStepLoading'

const REGISTER_STEP_DEFINITIONS: Array<
	Pick<OnboardingStepMeta, 'stepKey' | 'stepLabel'>
> = [
	{ stepKey: 'step_0', stepLabel: 'Method Selection' },
	{ stepKey: 'step_1', stepLabel: 'User info' },
	{ stepKey: 'step_2', stepLabel: 'Music Priority' },
	{ stepKey: 'step_3', stepLabel: 'Account info' },
	{ stepKey: 'step_4', stepLabel: 'Company Stores' },
	{ stepKey: 'step_5', stepLabel: 'Business category' },
	{ stepKey: 'step_6', stepLabel: 'Audience' },
	{ stepKey: 'step_7', stepLabel: 'Brand identity' },
]

const REGISTER_TOTAL_STEPS = REGISTER_STEP_DEFINITIONS.length - 1

// import { useCulturalIdentities } from '@/core/hooks/useCulturalIdentities'

interface RegisterUIProps {
	isLoading: boolean
	error: string | null
	isEmailAvailable: boolean | null
	isCheckingEmailAvailability?: boolean
	zoneCode?: string | null
	createdGroupId?: number | null
	createdZoneId?: number | null
	businessCategories: BusinessCategory[]
	demographicAgeGroups: DemographicAgeGroup[]
	// demographicGenders: DemographicGender[]
	demographicSocioeconomicGroups: DemographicSocioeconomicGroup[]
	form: UseFormReturn<RegisterRequestDTO>
	onFinish: (values: RegisterRequestDTO) => void
	/**
	 * When true, the parent route wants to prevent accidental exits
	 * (e.g. from steps 1–6 with dirty form state).
	 */
	shouldBlockExit?: boolean
	/**
	 * True when the registration API call completed successfully
	 */
	registrationComplete?: boolean
	/**
	 * Callback to navigate after loading simulation completes
	 */
	onNavigateAfterLoading?: () => void
}

export const RegisterUI: React.FC<RegisterUIProps> = ({
	isLoading,
	error,
	form,
	onFinish,
	isEmailAvailable,
	isCheckingEmailAvailability,
	businessCategories,
	demographicAgeGroups,
	// demographicGenders,
	demographicSocioeconomicGroups,
	shouldBlockExit,
	registrationComplete,
	onNavigateAfterLoading,
}) => {
	const { t: tCommon } = useTranslation('common')
	const [formData, setFormData] = useState<Partial<RegisterRequestDTO>>({})
	const { currentStep, goToStep, next, prev } = useRegisterFlow()

	const { data: brandIdentities } = useBrandIdentities()

	// const { data: culturalIdentities, isLoading: isCulturalIdentitiesLoading } =
	// 	useCulturalIdentities()

	const [pendingStep, setPendingStep] = useState<number | null>(null)
	const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [simulationComplete, setSimulationComplete] = useState(false)

	// Reset submitting state if there's an error
	useEffect(() => {
		if (error) {
			setIsSubmitting(false)
			setSimulationComplete(false)
		}
	}, [error])

	// Handle loading simulation completion callback
	const handleSimulationComplete = useCallback(() => {
		setSimulationComplete(true)
	}, [])

	// Navigate when both registration and simulation are complete
	useEffect(() => {
		if (registrationComplete && simulationComplete && onNavigateAfterLoading) {
			onNavigateAfterLoading()
		}
	}, [registrationComplete, simulationComplete, onNavigateAfterLoading])

	const currentStepMeta =
		REGISTER_STEP_DEFINITIONS[currentStep] ?? REGISTER_STEP_DEFINITIONS[0]

	useOnboardingStepTracking(currentStep, {
		stepKey: currentStepMeta.stepKey,
		stepLabel: currentStepMeta.stepLabel,
		totalSteps: REGISTER_TOTAL_STEPS,
	})

	const handleNextStep = (stepFormData: Partial<RegisterRequestDTO>) => {
		setFormData((prevData) => ({
			...prevData,
			...stepFormData,
		}))
		next()
	}

	const handleStepNavigation = (step: number) => {
		if (step <= currentStep) {
			if (shouldBlockExit) {
				setPendingStep(step)
				setShowUnsavedChangesModal(true)
				return
			}
			goToStep(step)
		}
	}

	const handlePrevStep = () => {
		prev()
	}

	const handleFormSubmit = async () => {
		try {
			const isValid = await form.trigger([
				'firstname',
				'lastname',
				'phone_number',
				'country',
				'parent_business_category_id',
				'business_category_id',
				'brand_name',
				'total_company_stores',
				// 'terms_accepted', // temporarily skipped (no UI control at the moment)
				'brand_identities',
				// 'cultural_identity_id',
				'demographic_ages',
				// 'demographic_gender',
				'demographic_socioeconomics',
			])

			if (isValid && isEmailAvailable === true) {
				setIsSubmitting(true)
				const registerValues = form.getValues()

				const { ...registerData } = registerValues as any

				onFinish({
					...formData,
					...registerData,
				} as RegisterRequestDTO)
			}
		} catch (error) {
			console.error(error)
			setIsSubmitting(false)
		}
	}

	// Show loading screen when submitting
	if (isSubmitting) {
		return (
			<RegisterStepLoading onSimulationComplete={handleSimulationComplete} />
		)
	}

	return (
		<OnbardingLayout
			shouldBlockExit={shouldBlockExit}
			hideImageOnStep={
				currentStep === 2 ||
				currentStep === 4 ||
				currentStep === 5 ||
				currentStep === 6 ||
				currentStep === 7
			}
		>
			<div
				className={
					currentStep === 4 ||
					currentStep === 5 ||
					currentStep === 6 ||
					currentStep === 7
						? 'w-full h-full max-w-4xl mx-auto'
						: 'max-w-md mx-auto'
				}
			>
				<Spinner spinning={isLoading}>
					{error && (
						<Alert variant="error" className="mb-4">
							<AlertTitle>{tCommon('common.error')}</AlertTitle>
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}

					<div
						className={
							currentStep === 4 ||
							currentStep === 5 ||
							currentStep === 6 ||
							currentStep === 7
								? 'flex space-x-1.5 mb-6'
								: 'hidden'
						}
					>
						<Badge
							variant={currentStep === 4 ? 'default' : 'secondary'}
							className={`h-2 p-0 cursor-pointer transition-all duration-300 ${
								currentStep === 4
									? 'w-6 bg-gray-800 dark:bg-white rounded-full'
									: 'w-2 bg-gray-400 dark:bg-white/40 rounded-full'
							}`}
							onClick={() => handleStepNavigation(4)}
						></Badge>
						<Badge
							variant={currentStep === 5 ? 'default' : 'secondary'}
							className={`h-2 p-0 cursor-pointer transition-all duration-300 ${
								currentStep === 5
									? 'w-6 bg-gray-800 dark:bg-white rounded-full'
									: 'w-2 bg-gray-400 dark:bg-white/40 rounded-full'
							}`}
							onClick={() => handleStepNavigation(5)}
						></Badge>
						<Badge
							variant={currentStep === 6 ? 'default' : 'secondary'}
							className={`h-2 p-0 cursor-pointer transition-all duration-300 ${
								currentStep === 6
									? 'w-6 bg-gray-800 dark:bg-white rounded-full'
									: 'w-2 bg-gray-400 dark:bg-white/40 rounded-full'
							}`}
							onClick={() => handleStepNavigation(6)}
						></Badge>
						<Badge
							variant={currentStep === 7 ? 'default' : 'secondary'}
							className={`h-2 p-0 cursor-pointer transition-all duration-300 ${
								currentStep === 7
									? 'w-6 bg-gray-800 dark:bg-white rounded-full'
									: 'w-2 bg-gray-400 dark:bg-white/40 rounded-full'
							}`}
							onClick={() => handleStepNavigation(7)}
						></Badge>
					</div>

					<div className="h-full flex flex-col">
						<div className="h-full flex-1 flex flex-col">
							{currentStep === 0 && (
								<RegisterStep0 onNextStep={() => handleNextStep({})} />
							)}
							{currentStep === 1 && (
								<RegisterStep1
									form={form}
									isEmailAvailable={isEmailAvailable}
									isCheckingEmailAvailability={isCheckingEmailAvailability}
									isLoading={isLoading}
									onNextStep={handleNextStep}
								/>
							)}
							{currentStep === 2 && (
								<RegisterStep2 onNextStep={() => handleNextStep({})} />
							)}
							{currentStep === 3 && (
								<RegisterStep3
									form={form}
									isLoading={isLoading}
									onNextStep={handleNextStep}
									onPrevStep={handlePrevStep}
								/>
							)}
							{currentStep === 4 && (
								<RegisterStep4
									form={form}
									isLoading={isLoading}
									onNextStep={handleNextStep}
									onPrevStep={handlePrevStep}
								/>
							)}
							{currentStep === 5 && (
								<RegisterStep5
									form={form}
									businessCategories={businessCategories}
									isLoading={isLoading}
									onNextStep={handleNextStep}
									onPrevStep={handlePrevStep}
								/>
							)}
							{currentStep === 6 && (
								<RegisterStep6
									form={form}
									isLoading={isLoading}
									onNextStep={handleNextStep}
									onPrevStep={handlePrevStep}
									demographicAgeGroups={demographicAgeGroups}
									demographicSocioeconomicGroups={
										demographicSocioeconomicGroups
									}
								/>
							)}
							{currentStep === 7 && (
								<RegisterStep7
									form={form}
									brandIdentities={brandIdentities?.data || []}
									isLoading={isLoading}
									onFormSubmit={handleFormSubmit}
									onPrevStep={handlePrevStep}
								/>
							)}
						</div>
					</div>
				</Spinner>
			</div>

			<ConfirmationModal
				visible={showUnsavedChangesModal}
				title={tCommon('common.unsavedChanges')}
				description={tCommon('common.unsavedChangesDescription')}
				confirmText={tCommon('actions.continue')}
				cancelText={tCommon('actions.cancel')}
				variant="default"
				onConfirm={() => {
					if (pendingStep !== null) {
						goToStep(pendingStep)
						setPendingStep(null)
					}
					setShowUnsavedChangesModal(false)
				}}
				onClose={() => {
					setPendingStep(null)
					setShowUnsavedChangesModal(false)
				}}
			/>
		</OnbardingLayout>
	)
}
